// Platform publicIds (plt_…) for the WPR-family platforms. `currentPlatform.id`
// is an opaque publicId string, so these predicates match against publicIds —
// the trailing comment records the legacy numeric id each one replaced.
const USPA_PUBLIC_ID = "plt_58573d6d5c0e101f"; // formerly 56
const PAC_PUBLIC_ID = "plt_3eb4b418e6b463ea"; // formerly 75
const NPL_PUBLIC_ID = "plt_2ed915a0fac2fe3e"; // formerly 50
const QUEBEC_PUBLIC_ID = "plt_65f5e05bc094d61f"; // formerly 87

const WPR_PUBLIC_IDS: string[] = [USPA_PUBLIC_ID, PAC_PUBLIC_ID, NPL_PUBLIC_ID, QUEBEC_PUBLIC_ID];

export const isWprPlatforms = (platformPublicId: string | null | undefined) => {
  if (!platformPublicId) return false;
  return WPR_PUBLIC_IDS.includes(platformPublicId);
};

export const isUspaPlatform = (platformPublicId: string | null | undefined) => {
  return Boolean(platformPublicId) && platformPublicId === USPA_PUBLIC_ID;
};

export const isPacPlatform = (platformPublicId: string | null | undefined) => {
  return Boolean(platformPublicId) && platformPublicId === PAC_PUBLIC_ID;
};

export const isQuebecPlatform = (platformPublicId: string | null | undefined) => {
  return Boolean(platformPublicId) && platformPublicId === QUEBEC_PUBLIC_ID;
};

// The main Tournated platform and RNKS.online, matched by publicId. These used
// to be detected via the numeric `env.tournatedPlatformId` / a hardcoded "14",
// which silently broke once platform ids became opaque publicId strings.
const TOURNATED_PUBLIC_ID = "plt_24e7c41e2066162f"; // formerly 1
const RNKS_PUBLIC_ID = "plt_56ae338c2bf8aded"; // formerly 14
const CTF_PUBLIC_ID = "plt_a9ebcecae37a2dad"; // formerly 25 — Cyprus Tennis Federation
const LTS_PUBLIC_ID = "plt_8adacf51fb2f4d24"; // formerly 7 — Latvijas Tenisa Savienība
const RTA_PUBLIC_ID = "plt_c9bb14398f6d6aef"; // formerly 30 — RTA Pickleball Tour
// Lithuania (legacy id 10). Matched by publicId and/or platformLink containing tennis.lt.
const LITHUANIA_PUBLIC_ID = "plt_4c1ef633896a1d2a"; // formerly 10 — Lietuvos teniso sąjunga
const LITHUANIA_WTN_LINK_HINT = "tennis.lt";

const WTN_PUBLIC_IDS: string[] = [LTS_PUBLIC_ID, CTF_PUBLIC_ID, LITHUANIA_PUBLIC_ID];

export const isTournatedPlatform = (platformPublicId: string | null | undefined) => {
  return Boolean(platformPublicId) && platformPublicId === TOURNATED_PUBLIC_ID;
};

export const isRnksPlatform = (platformPublicId: string | null | undefined) => {
  return Boolean(platformPublicId) && platformPublicId === RNKS_PUBLIC_ID;
};

export const isCtfPlatform = (platformPublicId: string | null | undefined) => {
  return Boolean(platformPublicId) && platformPublicId === CTF_PUBLIC_ID;
};

export const isRtaPlatform = (platformPublicId: string | null | undefined) => {
  return Boolean(platformPublicId) && platformPublicId === RTA_PUBLIC_ID;
};

/** WTN platforms: Latvia (LTS), Cyprus (CTF), Lithuania (play.tennis.lt). */
export const isWtnPlatform = (
  platformPublicId: string | null | undefined,
  platformLink?: string | null
) => {
  if (platformPublicId && WTN_PUBLIC_IDS.includes(platformPublicId)) return true;
  if (platformLink && platformLink.toLowerCase().includes(LITHUANIA_WTN_LINK_HINT)) return true;
  return false;
};

export type RankingsRatingProvider = "WTN" | "WPR" | "DUPR";

export const getRankingsRatingProvider = (platform: {
  id?: string | null;
  platformLink?: string | null;
  sports?: Array<{ title?: string | null }>;
}): RankingsRatingProvider | null => {
  // WTN before WPR: Lithuania (tennis.lt) / LTS / CTF must never show as WPR
  // even if platform id/link matching is ambiguous.
  if (isWtnPlatform(platform?.id, platform?.platformLink)) return "WTN";

  if (isWprPlatforms(platform?.id)) return "WPR";

  const sportTitles =
    platform?.sports?.map((s) => s?.title?.toLowerCase().trim()).filter(Boolean) ?? [];
  const isPickleball = sportTitles.includes("pickleball");
  if (isPickleball || isRtaPlatform(platform?.id)) return "DUPR";

  // Sport fallbacks when publicId is missing/stale (common on legacy rows).
  if (sportTitles.includes("tennis")) return "WTN";
  if (sportTitles.includes("padel")) return "WPR";

  return null;
};
